import { FC } from "react";
import { Formik, FormikHelpers } from "formik";
import Form from "react-bootstrap/esm/Form";
import Button from "react-bootstrap/esm/Button";
import { StatusAlert } from "app/component/form/StatusAlert";
import { handleError } from "app/api/graph/form";
import yup from "app/util/yup";
import {
    TestType,
    TestTypePrerequisiteFieldsFragment,
    useCreateTestTypePrerequisiteMutation,
} from "app/api/graph/types";
import { FormControlSelectTestType } from "app/component/form/control/FormControlSelectTestType";

export type FormValues = {
    testType: TestType;
    description: string;
};

export const schema: yup.ObjectSchema<FormValues> = yup.object({
    testType: yup.mixed<TestType>().oneOf(Object.values(TestType)).required(),
    description: yup.string().required(),
});

type Props = {
    onCreate: (testTypePrerequisite: TestTypePrerequisiteFieldsFragment) => void;
};

const initialValues: FormValues = {
    testType: TestType.NotSpecified,
    description: "",
};

export const CreateTestTypePrerequisiteForm: FC<Props> = ({ onCreate }) => {
    const [createTestTypePrerequisite] = useCreateTestTypePrerequisiteMutation();

    const onSubmit = (values: FormValues, actions: FormikHelpers<FormValues>) =>
        createTestTypePrerequisite({
            variables: {
                input: {
                    testType: values.testType,
                    description: values.description,
                },
            },
        })
            .then((response) =>
                response.data?.createTestTypePrerequisite?.testTypePrerequisite
                    ? onCreate?.(response.data.createTestTypePrerequisite.testTypePrerequisite)
                    : undefined,
            )
            .catch((error) => handleError(error, actions))
            .finally(() => actions.setSubmitting(false));

    return (
        <Formik validationSchema={schema} onSubmit={onSubmit} initialValues={initialValues}>
            {({
                handleSubmit,
                setFieldValue,
                setFieldTouched,
                values,
                isSubmitting,
                errors,
                touched,
                status,
                handleChange,
                handleBlur,
            }) => (
                <Form noValidate onSubmit={handleSubmit} autoComplete="off">
                    <StatusAlert status={status as unknown} />

                    <Form.Group className="mb-3">
                        <Form.Label>Test type</Form.Label>
                        <FormControlSelectTestType
                            name="testType"
                            value={values.testType}
                            onChange={(value) => setFieldValue("testType", value)}
                            onBlur={() => setFieldTouched("testType")}
                            isInvalid={touched.testType && !!errors.testType}
                        />
                        <Form.Control.Feedback type="invalid">{errors.testType}</Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            name="description"
                            value={values.description ?? ""}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.description && !!errors.description}
                            autoComplete="off"
                        />
                        <Form.Control.Feedback type="invalid">{errors.description}</Form.Control.Feedback>
                    </Form.Group>

                    <Button type="submit" disabled={isSubmitting}>
                        Submit
                    </Button>
                </Form>
            )}
        </Formik>
    );
};
